/**
 * RecommendedServicesCard — Iter-163 · Phase 3 + Iter-164 · Phase 4/6.
 *
 * Secondary "Recommended NECS Services" panel. Pulls live data:
 *   Multifamily  → GET /api/service-packages/public  (Signature Collection tier)
 *   Commercial   → GET /api/commercial/service-lines?visibility=commercial
 *
 * Filters:
 *   * Multifamily view surfaces the Signature Collection package and
 *     lists its services (from the tier catalogue in
 *     lib/servicePackages), pruning anything already contracted.
 *   * Commercial view resolves the property's business type
 *     (property.property_type) and shows ONLY the matching Professional
 *     Care vertical (e.g. Office → NECS Office Pro). Each vertical
 *     surfaces its dedicated services list.
 *
 * Iter-164 · Phase 6 — when the PM is on manual pay (autopay_status
 * absent / not ACTIVE), a gentle "Set Up Autopay" prompt renders at
 * the top and jumps to the PaymentMethodsPanel via `onExploreAutopay`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Sparkles, ChevronRight, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { activeSpecialtyServices } from '../lib/specialtyServices';

const API = process.env.REACT_APP_BACKEND_URL;

// Human-readable label for each of the 12 commercial verticals. Falls
// back to the property_label from the API when a key is unmapped.
const VERTICAL_LABEL = {
  office:        'Corporate Offices / Office Buildings',
  retail:        'Retail Stores / Retail Centers',
  gas_station:   'Gas Stations / Convenience Stores',
  school:        'Schools / Educational Facilities',
  medical:       'Medical Offices / Clinics',
  warehouse:     'Warehouses / Industrial Facilities',
  restaurant:    'Restaurants / Food-Service Facilities',
  church:        'Churches / Worship Facilities',
  mall:          'Shopping Centers / Malls',
  bank:          'Banking & Financial Facilities',
  stadium:       'Stadiums & Arenas',
  airport:       'Airports & Aviation Facilities',
};

export default function RecommendedServicesCard({
  property,
  accountType = 'multifamily',
  autopayActive: autopayActiveProp,   // optional override
  onExploreAutopay,             // click handler for the prompt CTA
  onExplorePackage,             // optional click handler per package
}) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autopayActive, setAutopayActive] = useState(Boolean(autopayActiveProp));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const endpoint = accountType === 'commercial'
          ? `${API}/api/commercial/service-lines?visibility=commercial`
          : `${API}/api/service-packages/public`;
        const r = await fetch(endpoint, { credentials: 'include' });
        if (r.ok && !cancelled) setPayload(await r.json());
      } catch (_) {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [accountType]);

  // Iter-164 · Phase 6 — resolve autopay status live so the prompt
  // hides immediately once the PM enrolls without a page reload.
  useEffect(() => {
    if (autopayActiveProp !== undefined) { setAutopayActive(Boolean(autopayActiveProp)); return; }
    if (!property?.property_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/iter156/autopay/status/${encodeURIComponent(property.property_id)}`,
                                { credentials: 'include' });
        if (r.ok && !cancelled) {
          const d = await r.json();
          setAutopayActive(Boolean(d?.autopay_active));
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [property?.property_id, autopayActiveProp]);

  // Contracted service slugs (for filtering multifamily upsells).
  const contractedSlugs = useMemo(() => new Set(
    (activeSpecialtyServices(property) || []).map((s) => s.slug),
  ), [property]);

  // Recommended packages to render.
  const recommendations = useMemo(() => {
    if (!payload) return [];
    if (accountType === 'commercial') {
      const propType = property?.property_type || property?.business_type || 'office';
      const lines = Array.isArray(payload.service_lines) ? payload.service_lines : [];
      const match = lines.find((l) => l.property_type === propType) || lines[0];
      if (!match) return [];
      // Show every tier of the matched vertical as a "recommended
      // package" so PMs see the full ladder including Professional
      // Care (the middle tier).
      return (match.tiers || []).map((t) => ({
        key: `${match.property_type}:${t.key}`,
        name: `${match.necs_name} · ${t.name}`,
        business_type: VERTICAL_LABEL[match.property_type] || match.property_label,
        frequency: t.frequency,
        services: t.services || [],
        included: t.included || [],
        tagline: match.tagline,
        vertical_key: match.property_type,
        // Iter-167 · Phase 2 — expose the raw tier key so the parent
        // can pre-select the matching tier row inside the wizard.
        tier_key: t.key,
      }));
    }
    // Multifamily — return the Signature Collection tier explicitly
    // (spec calls this out) plus any other tier the PM isn't on.
    const pkgs = Array.isArray(payload.packages) ? payload.packages : [];
    const target = pkgs.find((p) => (p.name || '').toLowerCase().includes('signature'))
      || pkgs[pkgs.length - 1];
    if (!target) return [];
    const currentName = (property?.package_type || property?.package_name || '').toLowerCase();
    if (currentName && target.name && target.name.toLowerCase() === currentName) return [];
    return [{
      key: target.key,
      name: target.name,
      business_type: 'Multifamily',
      monthly_price: target.monthly_price,
      services: (target.services || []).filter((s) => !contractedSlugs.has(s)),
      included: target.included || [],
      tagline: 'Our flagship multifamily tier — everything included.',
    }];
  }, [payload, accountType, property, contractedSlugs]);

  const showAutopayPrompt = accountType && !autopayActive; // gates on PM manual-pay only

  return (
    <div className="rounded-xl border p-4"
         style={{ backgroundColor: '#0f0f0f', borderColor: '#3f3f46' }}
         data-testid="recommended-services-card">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-zinc-400" />
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-300">
          Recommended NECS Services
        </p>
        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-500"
              data-testid="recommended-services-badge">Upsell</span>
      </div>

      {/* Iter-164 · Phase 6 — autopay enrollment prompt (only when
          onExploreAutopay is provided AND the PM is on manual pay). */}
      {showAutopayPrompt && onExploreAutopay && (
        <div className="rounded-lg border p-3 mb-3 flex items-start gap-2"
             style={{ backgroundColor: '#1e293b', borderColor: '#3b82f6' }}
             data-testid="autopay-enrollment-prompt">
          <Zap className="h-4 w-4 text-blue-300 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-xs text-blue-100 font-semibold">
              Save time — enroll in ACH or card autopay so invoices are paid automatically on the due date.
            </p>
            <Button size="sm" onClick={() => {
                    // Iter-165 · Phase 3 — analytics event on CTA click.
                    fetch(`${API}/api/analytics/autopay-event`, {
                      method: 'POST', credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ event_type: 'click_autopay_cta',
                                              property_id: property?.property_id }),
                    }).catch(() => {});
                    onExploreAutopay && onExploreAutopay();
                  }}
                    className="mt-2 text-[10px] uppercase font-bold tracking-widest"
                    style={{ backgroundColor: '#3b82f6', color: '#fff' }}
                    data-testid="autopay-prompt-cta">
              Set Up Autopay
            </Button>
          </div>
        </div>
      )}

      {loading && <p className="text-xs text-zinc-500" data-testid="recommended-services-loading">Loading…</p>}
      {!loading && recommendations.length === 0 && (
        <p className="text-xs text-zinc-500" data-testid="recommended-services-empty">
          Your account is on our top tier — no additional packages to recommend.
        </p>
      )}
      <ul className="space-y-3">
        {recommendations.slice(0, 4).map((r) => (
          <li key={r.key}
              className="group rounded border p-3 hover:bg-white/[0.03] transition"
              style={{ backgroundColor: '#141414', borderColor: '#27272a' }}
              data-testid={`recommended-service-${r.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white" data-testid={`recommended-name-${r.key}`}>{r.name}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5"
                   data-testid={`recommended-vertical-${r.key}`}>{r.business_type}</p>
                {r.tagline && <p className="text-[10px] text-zinc-500 mt-1">{r.tagline}</p>}
                {r.frequency && (
                  <p className="text-[10px] text-zinc-500 mt-1">Frequency: <span className="text-zinc-300">{r.frequency}</span></p>
                )}
                {/* Iter-190 · Price intentionally hidden on the PM
                    dashboard's Recommended NECS Services per CEO
                    directive Feb 2026 — pricing lives on the quote
                    wizard, never as a static teaser here.
                    Iter-191 · Hover reveals a "See a live quote →"
                    hint so PMs know exactly one click reaches the
                    wizard, without ever printing static prices. */}
                <p className="text-[10px] mt-1 opacity-0 group-hover:opacity-100 transition-opacity text-[#C9A962] font-bold uppercase tracking-widest">
                  See a live quote →
                </p>
                {(r.included && r.included.length > 0) && (
                  <ul className="mt-2 space-y-0.5">
                    {r.included.slice(0, 6).map((line, i) => (
                      <li key={i} className="text-[10px] text-zinc-400">• {line}</li>
                    ))}
                  </ul>
                )}
                {(!r.included || r.included.length === 0) && r.services && r.services.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {r.services.slice(0, 8).map((s) => (
                      <span key={s} className="text-[9px] uppercase px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: '#27272a', color: '#e5e5e5' }}>
                        {String(s).replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button size="sm" onClick={() => onExplorePackage && onExplorePackage(r)}
                      className="text-[10px] uppercase font-bold tracking-widest"
                      style={{ backgroundColor: '#C9A962', color: '#111' }}
                      data-testid={`recommended-explore-${r.key}`}>
                Explore <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
